import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai/react";

import { LanguageType } from "@libs/i18n";
import { UpdateCurrentPatientType } from "@/domain/patients";
import mixpanel from "@/services/mixpanel";
import { pendingLanguageAtom } from "@/stores/atoms";

import { PatientsApi } from "../api/patientsApi";
import { PatientsQueryKeys } from "../queries/patientsQueries";

export const useUpdateCurrentPatientMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      patientData,
    }: {
      patientData: UpdateCurrentPatientType;
    }) => PatientsApi.updateCurrentPatient(patientData),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: PatientsQueryKeys.me(),
      });
    },
  });
};

// Keep cleanup in the mutation lifecycle so it survives caller unmounts.
export const useSyncPreferredLanguageMutation = () => {
  const queryClient = useQueryClient();
  const setPendingLanguage = useSetAtom(pendingLanguageAtom);

  return useMutation({
    mutationFn: async ({
      language,
    }: {
      language: LanguageType;
      shouldTrack?: boolean;
    }) => PatientsApi.updateCurrentPatient({ preferredLanguage: language }),
    onSuccess: (updatedPatient, { language, shouldTrack = true }) => {
      // Update the cache before clearing pending to avoid reapplying a stale locale.
      if (updatedPatient) {
        queryClient.setQueryData(PatientsQueryKeys.me(), updatedPatient);
      }
      setPendingLanguage(null);
      if (shouldTrack) {
        mixpanel.track("Language Changed", { language });
      }
    },
  });
};
